/**
 * 坐标系转换工具
 *
 * 项目约定：
 *   - 数据库内部统一存储坐标系由配置中心 MAP_DOMAIN.STORE_COORD 决定，默认 GCJ02。
 *   - 后端 Web 端切换到天地图后，地图原生使用 WGS84/CGCS2000；
 *     若 STORE_COORD = GCJ02，则需要在显示前 gcj02 -> wgs84，保存时 wgs84 -> gcj02。
 *   - 前端 H5 巡检打卡仍走腾讯地图（GCJ02），由前端直接提交 GCJ02。
 */

const PI = 3.1415926535897932384626
// 长半轴（克拉索夫斯基椭球）
const A = 6378245.0
const EE = 0.00669342162296594323

function outOfChina(lng, lat) {
  const lon = +lng
  const lat2 = +lat
  return !(lon > 73.66 && lon < 135.05 && lat2 > 3.86 && lat2 < 53.55)
}

function transformLat(lng, lat) {
  let ret =
    -100.0 +
    2.0 * lng +
    3.0 * lat +
    0.2 * lat * lat +
    0.1 * lng * lat +
    0.2 * Math.sqrt(Math.abs(lng))
  ret += ((20.0 * Math.sin(6.0 * lng * PI) + 20.0 * Math.sin(2.0 * lng * PI)) * 2.0) / 3.0
  ret += ((20.0 * Math.sin(lat * PI) + 40.0 * Math.sin((lat / 3.0) * PI)) * 2.0) / 3.0
  ret += ((160.0 * Math.sin((lat / 12.0) * PI) + 320 * Math.sin((lat * PI) / 30.0)) * 2.0) / 3.0
  return ret
}

function transformLng(lng, lat) {
  let ret =
    300.0 + lng + 2.0 * lat + 0.1 * lng * lng + 0.1 * lng * lat + 0.1 * Math.sqrt(Math.abs(lng))
  ret += ((20.0 * Math.sin(6.0 * lng * PI) + 20.0 * Math.sin(2.0 * lng * PI)) * 2.0) / 3.0
  ret += ((20.0 * Math.sin(lng * PI) + 40.0 * Math.sin((lng / 3.0) * PI)) * 2.0) / 3.0
  ret += ((150.0 * Math.sin((lng / 12.0) * PI) + 300.0 * Math.sin((lng / 30.0) * PI)) * 2.0) / 3.0
  return ret
}

/**
 * WGS84 -> GCJ02（火星坐标系）
 */
export function wgs84ToGcj02(lng, lat) {
  const lon = +lng
  const lat2 = +lat
  if (outOfChina(lon, lat2)) {
    return { lng: lon, lat: lat2 }
  }
  let dlat = transformLat(lon - 105.0, lat2 - 35.0)
  let dlng = transformLng(lon - 105.0, lat2 - 35.0)
  const radlat = (lat2 / 180.0) * PI
  let magic = Math.sin(radlat)
  magic = 1 - EE * magic * magic
  const sqrtmagic = Math.sqrt(magic)
  dlat = (dlat * 180.0) / (((A * (1 - EE)) / (magic * sqrtmagic)) * PI)
  dlng = (dlng * 180.0) / ((A / sqrtmagic) * Math.cos(radlat) * PI)
  return { lng: lon + dlng, lat: lat2 + dlat }
}

/**
 * GCJ02 -> WGS84（近似反解，精度足够地图显示用）
 */
export function gcj02ToWgs84(lng, lat) {
  const lon = +lng
  const lat2 = +lat
  if (outOfChina(lon, lat2)) {
    return { lng: lon, lat: lat2 }
  }
  let dlat = transformLat(lon - 105.0, lat2 - 35.0)
  let dlng = transformLng(lon - 105.0, lat2 - 35.0)
  const radlat = (lat2 / 180.0) * PI
  let magic = Math.sin(radlat)
  magic = 1 - EE * magic * magic
  const sqrtmagic = Math.sqrt(magic)
  dlat = (dlat * 180.0) / (((A * (1 - EE)) / (magic * sqrtmagic)) * PI)
  dlng = (dlng * 180.0) / ((A / sqrtmagic) * Math.cos(radlat) * PI)
  return { lng: lon * 2 - (lon + dlng), lat: lat2 * 2 - (lat2 + dlat) }
}

/**
 * 把"数据库坐标"转成"天地图显示坐标"（WGS84）。
 * @param {number|string} lng
 * @param {number|string} lat
 * @param {string} storeCoord 存储坐标系 GCJ02 / WGS84，默认 GCJ02
 */
export function storeToTianditu(lng, lat, storeCoord = 'GCJ02') {
  const lon = parseFloat(lng)
  const lat2 = parseFloat(lat)
  if (Number.isNaN(lon) || Number.isNaN(lat2)) {
    return { lng: 0, lat: 0 }
  }
  if (String(storeCoord).toUpperCase() === 'WGS84') {
    return { lng: lon, lat: lat2 }
  }
  return gcj02ToWgs84(lon, lat2)
}

/**
 * 把"天地图选点坐标"（WGS84）转成"数据库坐标"。
 */
export function tiandituToStore(lng, lat, storeCoord = 'GCJ02') {
  const lon = parseFloat(lng)
  const lat2 = parseFloat(lat)
  if (Number.isNaN(lon) || Number.isNaN(lat2)) {
    return { lng: 0, lat: 0 }
  }
  if (String(storeCoord).toUpperCase() === 'WGS84') {
    return { lng: lon, lat: lat2 }
  }
  return wgs84ToGcj02(lon, lat2)
}
